import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { Button, Badge, Modal, FormInput, ConfirmDialog } from '../components/ui';
import StakeTable, { Column } from './GameEngine/StakeTable';
import { toast } from 'react-toastify';
import { Shield, CheckSquare, Square, Trash2, Edit } from 'lucide-react';

interface Role {
  id: number;
  name: string;
  permissions: Record<string, boolean>;
}

const AVAILABLE_PERMISSIONS = [
  { category: 'General', key: 'dashboard', label: 'Access Dashboard' },
  { category: 'General', key: 'users', label: 'Manage Users & Profiles' },
  { category: 'General', key: 'notifications', label: 'Manage Notifications' },

  { category: 'Team Management', key: 'roles', label: 'Role Management' },
  { category: 'Team Management', key: 'team', label: 'Team Members' },

  { category: 'Offers Management', key: 'campaigns', label: 'Campaign Builder' },
  { category: 'Offers Management', key: 'affiliate_settings', label: 'Affiliate Settings' },
  { category: 'Offers Management', key: 'vip_levels', label: 'VIP Levels' },

  { category: 'Manage Payment', key: 'withdrawals', label: 'Withdrawal' },
  { category: 'Manage Payment', key: 'deposits', label: 'Deposit' },

  { category: 'CMS Management', key: 'cms_pages', label: 'Pages Content' },
  { category: 'CMS Management', key: 'cms_banners', label: 'Sliders & Banners' },
  { category: 'CMS Management', key: 'cms_logos', label: 'Logos & Branding' },
  { category: 'CMS Management', key: 'cms_footer', label: 'Footer Settings' },
  { category: 'CMS Management', key: 'cms_social', label: 'Social Links' },
  { category: 'CMS Management', key: 'cms_help', label: 'Help Center' },
  { category: 'CMS Management', key: 'cms_game_cards', label: 'Home Game Cards' },
  { category: 'CMS Management', key: 'cms_affiliate', label: 'Affiliate CMS' },

  { category: 'Analytics & Reports', key: 'reports_users', label: 'User Reports' },
  { category: 'Analytics & Reports', key: 'reports_financial', label: 'Financial & Bonus Logs' },
  { category: 'Analytics & Reports', key: 'reports_games', label: 'Global Game History' },
  { category: 'Analytics & Reports', key: 'reports_profit', label: 'GGR & Analytics' },

  { category: 'Settings', key: 'settings_general', label: 'Website Setup' },
  { category: 'Settings', key: 'settings_auth', label: 'Auth Requirements' },
  { category: 'Settings', key: 'settings_gateway', label: 'Gateway Keys' },

  { category: 'Game Engine', key: 'game_engine_aggregators', label: 'Aggregators' },
  { category: 'Game Engine', key: 'game_engine_providers', label: 'Providers' },
  { category: 'Game Engine', key: 'game_engine_categories', label: 'Categories' },
  { category: 'Game Engine', key: 'game_engine_groups', label: 'Groups' },
  { category: 'Game Engine', key: 'game_engine_games', label: 'Games' },
];

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [selectedPerms, setSelectedPerms] = useState<Record<string, boolean>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteRoleId, setDeleteRoleId] = useState<number | null>(null);

  // Pagination and search state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchRoles();
  }, [page, limit, searchTerm]);

  const fetchRoles = async () => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * limit;
      let url = `/collaborator/roles?limit=${limit}&offset=${offset}`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await apiClient.get(url);
      if (response.data.status) {
        setRoles(response.data.data || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      toast.error('Failed to load roles.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setRoleName('');
    const initialPerms: Record<string, boolean> = {};
    AVAILABLE_PERMISSIONS.forEach(p => { initialPerms[p.key] = false; });
    setSelectedPerms(initialPerms);
    setShowModal(true);
  };

  const handleOpenEdit = (role: Role) => {
    setEditingId(role.id);
    setRoleName(role.name);
    const initialPerms: Record<string, boolean> = {};
    AVAILABLE_PERMISSIONS.forEach(p => { initialPerms[p.key] = !!role.permissions?.[p.key]; });
    setSelectedPerms(initialPerms);
    setShowModal(true);
  };

  const handleTogglePerm = (key: string) => {
    setSelectedPerms(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roleName.trim()) { toast.error('Role name is required.'); return; }

    try {
      if (editingId) {
        const res = await apiClient.put(`/collaborator/roles/${editingId}`, { name: roleName, permissions: selectedPerms });
        if (res.data.status) {
          toast.success('Role updated successfully.');
          fetchRoles();
        }
      } else {
        const res = await apiClient.post('/collaborator/roles', { name: roleName, permissions: selectedPerms });
        if (res.data.status) {
          toast.success('Role created successfully.');
          setPage(1);
          fetchRoles();
        }
      }
      setShowModal(false);
    } catch (error) {
      console.error('Failed to save role:', error);
      toast.error('Failed to save role.');
    }
  };

  const handleConfirmDeleteRole = async () => {
    if (!deleteRoleId) return;
    try {
      const res = await apiClient.delete(`/collaborator/roles/${deleteRoleId}`);
      if (res.data.status) {
        toast.success('Role deleted.');
        fetchRoles();
      }
    } catch (error) {
      console.error('Failed to delete role:', error);
      toast.error('Failed to delete role.');
    } finally {
      setDeleteRoleId(null);
    }
  };

  const columns: Column[] = [
    { key: 'sn', label: 'S.No.', render: (_, __, i) => (page - 1) * limit + i + 1 },
    { key: 'name', label: 'Role Name' },
    {
      key: 'permissions',
      label: 'Permissions Granted',
      render: (perms: Record<string, boolean>) => {
        const active = Object.keys(perms || {}).filter(k => perms[k]);
        if (active.length === 0) return <Badge variant="danger">None</Badge>;
        return (
          <div className="flex flex-wrap gap-1 max-w-[500px]">
            {active.map(k => (
              <Badge key={k} variant="success">
                {AVAILABLE_PERMISSIONS.find(p => p.key === k)?.label || k}
              </Badge>
            ))}
          </div>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, item: Role) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(item)}>
            <Edit size={14} /> Edit
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDeleteRoleId(item.id)}>
            <Trash2 size={14} /> Delete
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="ml-0 lg:ml-[260px] p-4 sm:p-6 text-white min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white">Role & Permission Matrix</h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">Define customizable roles and their access privileges</p>
        </div>
      </div>

      <StakeTable
        title=""
        actions={
          <Button onClick={handleOpenCreate} className="flex items-center justify-center gap-2 shrink-0">
            <Shield size={16} /> Add New Role
          </Button>
        }
        columns={columns}
        data={roles}
        isLoading={isLoading}
        emptyMessage="No roles created yet. Click 'Add New Role' to get started."
        hasSidebarMargin={false}
        page={page}
        totalPages={Math.ceil(total / limit)}
        total={total}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        onSearch={(val) => { setSearchTerm(val); setPage(1); }}
        searchPlaceholder="Search roles..."
      />

      <Modal
        open={showModal}
        title={editingId ? 'Edit Role' : 'Create Role'}
        onClose={() => setShowModal(false)}
      >
        <form onSubmit={handleSave} className="flex flex-col gap-5">
          <FormInput
            label="Role Name"
            placeholder="e.g. Finance Administrator"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            required
          />

          <div>
            <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-3">
              <label className="block text-xs sm:text-sm font-semibold text-slate-300">
                Permissions Matrix
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const updated: Record<string, boolean> = {};
                    AVAILABLE_PERMISSIONS.forEach(p => { updated[p.key] = true; });
                    setSelectedPerms(updated);
                  }}
                  className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold cursor-pointer"
                >
                  Select All
                </button>
                <span className="text-slate-600 text-xs">|</span>
                <button
                  type="button"
                  onClick={() => setSelectedPerms({})}
                  className="text-xs text-slate-400 hover:text-slate-300 font-semibold cursor-pointer"
                >
                  Deselect All
                </button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto pr-2 flex flex-col gap-4 custom-thin-scrollbar">
              {Object.entries(
                AVAILABLE_PERMISSIONS.reduce((acc, p) => {
                  if (!acc[p.category]) acc[p.category] = [];
                  acc[p.category].push(p);
                  return acc;
                }, {} as Record<string, typeof AVAILABLE_PERMISSIONS>)
              ).map(([category, perms]) => (
                <div key={category} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-1">
                    <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-wider">{category}</h4>
                    <div className="flex gap-2 text-[10px]">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPerms(prev => {
                            const copy = { ...prev };
                            perms.forEach(p => { copy[p.key] = true; });
                            return copy;
                          });
                        }}
                        className="text-emerald-400 hover:text-emerald-300 font-semibold cursor-pointer"
                      >
                        All
                      </button>
                      <span className="text-slate-600">|</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPerms(prev => {
                            const copy = { ...prev };
                            perms.forEach(p => { delete copy[p.key]; });
                            return copy;
                          });
                        }}
                        className="text-slate-400 hover:text-slate-300 font-semibold cursor-pointer"
                      >
                        None
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {perms.map(p => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => handleTogglePerm(p.key)}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border text-left cursor-pointer transition-all duration-150 ${
                          selectedPerms[p.key]
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                            : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                        }`}
                      >
                        {selectedPerms[p.key] ? <CheckSquare size={16} /> : <Square size={16} />}
                        <span className="text-xs font-semibold">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {editingId ? 'Save Changes' : 'Create Role'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleteRoleId !== null}
        onClose={() => setDeleteRoleId(null)}
        onConfirm={handleConfirmDeleteRole}
        title="Delete Role"
        message="Are you sure you want to delete this role?"
        confirmLabel="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default RoleManagement;
